import {
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
  ClientApiError,
  postJson,
  safeReturnToFromLocation,
  type ApiProblem,
} from "../auth/api";
import { TurnstileWidget } from "../auth/TurnstileWidget";
import { usePublicConfig } from "../auth/usePublicConfig";
import { Checkbox } from "../components/ui/Checkbox";
import { Button, Input, Skeleton } from "../components/ui";

function ErrorSummary({ problem }: { problem: ApiProblem | null }) {
  if (!problem) return null;
  return (
    <div className="form-alert form-alert--error" role="alert" tabIndex={-1}>
      <strong>{problem.message}</strong>
      {problem.fieldErrors && Object.keys(problem.fieldErrors).length > 0 ? (
        <ul>
          {Object.values(problem.fieldErrors).map((message) => <li key={message}>{message}</li>)}
        </ul>
      ) : null}
      <small>Mã yêu cầu: {problem.requestId}</small>
    </div>
  );
}

function SuccessMessage({ children }: { children: string }) {
  return <div className="form-alert form-alert--success" role="status">{children}</div>;
}

function useProblem() {
  const [problem, setProblem] = useState<ApiProblem | null>(null);
  const capture = useCallback((error: unknown) => {
    if (error instanceof ClientApiError) setProblem(error.problem);
    else setProblem({ code: "REQUEST_FAILED", message: "Không thể xử lý yêu cầu.", requestId: "unknown" });
  }, []);
  return { problem, setProblem, capture };
}

function AuthCard({
  eyebrow,
  title,
  description,
  children,
}: {
  eyebrow: string;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="auth-page">
      <div className="auth-card">
        <header className="auth-heading">
          <p className="eyebrow">{eyebrow}</p>
          <h1>{title}</h1>
          <p>{description}</p>
        </header>
        {children}
      </div>
      <aside className="auth-aside">
        <p className="eyebrow">Tài khoản WorkingDog VN</p>
        <h2>Một danh tính, dữ liệu được kiểm soát.</h2>
        <p>
          Tài khoản dùng internal ID và public slug tự sinh. Email, số điện thoại và ngày sinh
          không xuất hiện trong hồ sơ công khai.
        </p>
        <ul className="auth-benefits">
          <li>Session cookie HttpOnly, không lưu token trong trình duyệt.</li>
          <li>Đăng xuất từng thiết bị hoặc toàn bộ thiết bị.</li>
          <li>Google OAuth chỉ liên kết sau khi xác minh quyền sở hữu tài khoản.</li>
        </ul>
      </aside>
    </section>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const { config, error: configError } = usePublicConfig();
  const { problem, setProblem, capture } = useProblem();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const returnTo = useMemo(safeReturnToFromLocation, []);
  const onTurnstile = useCallback((token: string) => setTurnstileToken(token), []);
  const onTurnstileError = useCallback((message: string) => {
    setProblem({ code: "TURNSTILE_FAILED", message, requestId: "client" });
  }, [setProblem]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setProblem(null);
    setSubmitting(true);
    try {
      const response = await postJson<{ ok: boolean; onboardingRequired: boolean }>(
        "/api/v1/auth/login",
        { email, password, mfaCode: mfaCode || undefined, turnstileToken },
      );
      await refresh();
      navigate(response.onboardingRequired ? "/member/onboarding" : returnTo, { replace: true });
    } catch (error) {
      if (error instanceof ClientApiError && error.problem.code === "MFA_REQUIRED") {
        setMfaRequired(true);
      }
      capture(error);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard
      eyebrow="Đăng nhập"
      title="Truy cập không gian thành viên"
      description="Dùng email đã xác minh. Hệ thống không sử dụng username."
    >
      {configError ? <div className="form-alert form-alert--error">{configError}</div> : null}
      <ErrorSummary problem={problem} />
      <form className="auth-form" onSubmit={submit} noValidate>
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={problem?.fieldErrors?.email}
          required
        />
        <Input
          label="Mật khẩu"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={problem?.fieldErrors?.password}
          required
        />
        {mfaRequired ? (
          <Input
            label="Mã xác thực 2 bước"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={mfaCode}
            onChange={(event) => setMfaCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
            error={problem?.fieldErrors?.mfaCode}
            required
          />
        ) : null}
        {config ? (
          <TurnstileWidget
            siteKey={config.turnstileSiteKey}
            required={config.turnstileRequired}
            onToken={onTurnstile}
            onError={onTurnstileError}
          />
        ) : <Skeleton lines={1} />}
        <Button type="submit" disabled={submitting}>
          {submitting ? "Đang đăng nhập…" : "Đăng nhập"}
        </Button>
      </form>
      {config?.googleOAuthEnabled ? (
        <a className="oauth-button" href={`/api/v1/auth/google/start?returnTo=${encodeURIComponent(returnTo)}`}>
          Tiếp tục với Google
        </a>
      ) : null}
      <div className="auth-links">
        <Link to="/forgot-password">Quên mật khẩu?</Link>
        <Link to="/register">Tạo tài khoản</Link>
      </div>
    </AuthCard>
  );
}

export function RegisterPage() {
  const { config, error: configError } = usePublicConfig();
  const { problem, setProblem, capture } = useProblem();
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const onTurnstile = useCallback((token: string) => setTurnstileToken(token), []);
  const onTurnstileError = useCallback((message: string) => {
    setProblem({ code: "TURNSTILE_FAILED", message, requestId: "client" });
  }, [setProblem]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setProblem(null);
    setSuccess(null);
    if (password !== confirmPassword) {
      setProblem({
        code: "VALIDATION_ERROR",
        message: "Dữ liệu chưa hợp lệ.",
        requestId: "client",
        fieldErrors: { confirmPassword: "Mật khẩu nhập lại không khớp." },
      });
      return;
    }
    setSubmitting(true);
    try {
      const response = await postJson<{ message: string }>("/api/v1/auth/register", {
        displayName,
        email,
        password,
        turnstileToken,
      });
      setSuccess(response.message);
      setPassword("");
      setConfirmPassword("");
    } catch (error) {
      capture(error);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard
      eyebrow="Đăng ký"
      title="Tạo tài khoản thành viên"
      description="Sau khi xác minh email, bạn sẽ hoàn tất thông tin bắt buộc và chấp nhận điều khoản."
    >
      {configError ? <div className="form-alert form-alert--error">{configError}</div> : null}
      <ErrorSummary problem={problem} />
      {success ? <SuccessMessage>{success}</SuccessMessage> : null}
      <form className="auth-form" onSubmit={submit} noValidate>
        <Input
          label="Tên hiển thị"
          autoComplete="name"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          error={problem?.fieldErrors?.displayName}
          required
        />
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={problem?.fieldErrors?.email}
          required
        />
        <Input
          label="Mật khẩu"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          hint="12–128 ký tự, có ít nhất 3 nhóm chữ thường, chữ hoa, số, ký tự đặc biệt."
          error={problem?.fieldErrors?.password}
          required
        />
        <Input
          label="Nhập lại mật khẩu"
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          error={problem?.fieldErrors?.confirmPassword}
          required
        />
        {config ? (
          <TurnstileWidget
            siteKey={config.turnstileSiteKey}
            required={config.turnstileRequired}
            onToken={onTurnstile}
            onError={onTurnstileError}
          />
        ) : <Skeleton lines={1} />}
        <Button type="submit" disabled={submitting}>
          {submitting ? "Đang tạo tài khoản…" : "Tạo tài khoản"}
        </Button>
      </form>
      {config?.googleOAuthEnabled ? (
        <a className="oauth-button" href="/api/v1/auth/google/start?returnTo=%2Fmember%2Fonboarding">
          Đăng ký bằng Google
        </a>
      ) : null}
      <div className="auth-links"><Link to="/login">Đã có tài khoản? Đăng nhập</Link></div>
    </AuthCard>
  );
}

export function VerifyEmailPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const { problem, setProblem, capture } = useProblem();
  const tokenRef = useRef<string | null>(null);
  const started = useRef(false);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);

  if (tokenRef.current === null) {
    tokenRef.current = new URLSearchParams(window.location.search).get("token");
    if (tokenRef.current) window.history.replaceState({}, "", window.location.pathname);
  }

  useEffect(() => {
    const token = tokenRef.current;
    if (!token || started.current) return;
    started.current = true;
    setVerifying(true);
    void postJson<{ ok: boolean; onboardingRequired: boolean }>("/api/v1/auth/verify-email", { token })
      .then(async () => {
        await refresh();
        navigate("/member/onboarding", { replace: true });
      })
      .catch(capture)
      .finally(() => setVerifying(false));
  }, [capture, navigate, refresh]);

  async function resend(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setProblem(null);
    setMessage(null);
    try {
      const response = await postJson<{ message: string }>("/api/v1/auth/resend-verification", { email });
      setMessage(response.message);
    } catch (error) {
      capture(error);
    }
  }

  return (
    <AuthCard
      eyebrow="Xác minh email"
      title={verifying ? "Đang xác minh…" : "Kiểm tra hộp thư của bạn"}
      description="Email chưa xác minh không thể đăng nhập hoặc hoàn tất onboarding."
    >
      {verifying ? <Skeleton lines={3} /> : null}
      <ErrorSummary problem={problem} />
      {message ? <SuccessMessage>{message}</SuccessMessage> : null}
      {!tokenRef.current || problem ? (
        <form className="auth-form" onSubmit={resend}>
          <Input
            label="Email cần gửi lại"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            error={problem?.fieldErrors?.email}
            required
          />
          <Button type="submit">Gửi lại email xác minh</Button>
        </form>
      ) : null}
      <div className="auth-links"><Link to="/login">Về đăng nhập</Link></div>
    </AuthCard>
  );
}

export function ForgotPasswordPage() {
  const { config } = usePublicConfig();
  const { problem, setProblem, capture } = useProblem();
  const [email, setEmail] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const onTurnstile = useCallback((token: string) => setTurnstileToken(token), []);
  const onTurnstileError = useCallback((messageText: string) => {
    setProblem({ code: "TURNSTILE_FAILED", message: messageText, requestId: "client" });
  }, [setProblem]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setProblem(null);
    setMessage(null);
    setSubmitting(true);
    try {
      const response = await postJson<{ message: string }>("/api/v1/auth/forgot-password", {
        email,
        turnstileToken,
      });
      setMessage(response.message);
    } catch (error) {
      capture(error);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard
      eyebrow="Khôi phục tài khoản"
      title="Quên mật khẩu"
      description="Phản hồi luôn giống nhau dù email có tồn tại hay không để bảo vệ danh tính thành viên."
    >
      <ErrorSummary problem={problem} />
      {message ? <SuccessMessage>{message}</SuccessMessage> : null}
      <form className="auth-form" onSubmit={submit}>
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={problem?.fieldErrors?.email}
          required
        />
        {config ? (
          <TurnstileWidget
            siteKey={config.turnstileSiteKey}
            required={config.turnstileRequired}
            onToken={onTurnstile}
            onError={onTurnstileError}
          />
        ) : null}
        <Button type="submit" disabled={submitting}>
          {submitting ? "Đang gửi…" : "Gửi hướng dẫn"}
        </Button>
      </form>
      <div className="auth-links"><Link to="/login">Về đăng nhập</Link></div>
    </AuthCard>
  );
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const { problem, setProblem, capture } = useProblem();
  const tokenRef = useRef<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (tokenRef.current === null) {
    tokenRef.current = new URLSearchParams(window.location.search).get("token");
    if (tokenRef.current) window.history.replaceState({}, "", window.location.pathname);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setProblem(null);
    if (!tokenRef.current) {
      setProblem({ code: "TOKEN_MISSING", message: "Liên kết đặt lại mật khẩu không hợp lệ.", requestId: "client" });
      return;
    }
    if (password !== confirmPassword) {
      setProblem({
        code: "VALIDATION_ERROR",
        message: "Dữ liệu chưa hợp lệ.",
        requestId: "client",
        fieldErrors: { confirmPassword: "Mật khẩu nhập lại không khớp." },
      });
      return;
    }
    setSubmitting(true);
    try {
      await postJson("/api/v1/auth/reset-password", { token: tokenRef.current, password });
      navigate("/login?reset=success", { replace: true });
    } catch (error) {
      capture(error);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard
      eyebrow="Mật khẩu mới"
      title="Đặt lại mật khẩu"
      description="Sau khi cập nhật, toàn bộ phiên đăng nhập hiện có sẽ bị thu hồi."
    >
      <ErrorSummary problem={problem} />
      <form className="auth-form" onSubmit={submit}>
        <Input
          label="Mật khẩu mới"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={problem?.fieldErrors?.password}
          required
        />
        <Input
          label="Nhập lại mật khẩu"
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          error={problem?.fieldErrors?.confirmPassword}
          required
        />
        <Button type="submit" disabled={submitting}>
          {submitting ? "Đang cập nhật…" : "Cập nhật mật khẩu"}
        </Button>
      </form>
    </AuthCard>
  );
}

export function TermsAcceptance({
  accepted,
  setAccepted,
  error,
}: {
  accepted: boolean;
  setAccepted: (value: boolean) => void;
  error?: string;
}) {
  return (
    <Checkbox
      checked={accepted}
      onChange={setAccepted}
      label="Tôi đồng ý với Điều khoản sử dụng và Chính sách quyền riêng tư"
      description="Việc chấp nhận được ghi nhận theo phiên bản, thời gian và request ID để phục vụ kiểm toán."
      error={error}
    />
  );
}
